import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Palette, Plus, Trash2, Pencil, Save, X, ExternalLink, Beaker, Upload, FileDown } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["color", "developer", "lightener", "toner", "additive"];
const UNITS = ["g", "ml", "oz"];
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const blank = { name: "", brand: "", type: "color", shade: "", size_amount: "", size_unit: "g", cost: "" };

export default function ColorLibraryPage() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [waste, setWaste] = useState(null);
  const [wasteDays, setWasteDays] = useState(7);
  const [multiplier, setMultiplier] = useState(2.75);
  const [multInput, setMultInput] = useState("2.75");
  const [editing, setEditing] = useState(null); // product_id | 'new'
  const [draft, setDraft] = useState(blank);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/color-products"),
        api.get("/color-bar/settings"),
      ]);
      setProducts(p.data || []);
      setMultiplier(s.data?.charge_multiplier || 2.75);
      setMultInput(String(s.data?.charge_multiplier || 2.75));
    } catch (e) { toast.error("Couldn't load color library"); }
  }, []);

  const loadFormulas = useCallback(async () => {
    try { const r = await api.get("/color-formulas?limit=100"); setFormulas(r.data || []); } catch { /* */ }
  }, []);

  const loadWaste = useCallback(async (d) => {
    try { const r = await api.get(`/color-bar/waste-report?days=${d}`); setWaste(r.data); } catch { /* */ }
  }, []);

  useEffect(() => { load(); loadFormulas(); }, [load, loadFormulas]);
  useEffect(() => { if (tab === "waste") loadWaste(wasteDays); }, [tab, wasteDays, loadWaste]);

  const startNew = () => { setDraft(blank); setEditing("new"); };
  const startEdit = (p) => { setDraft({ ...p, size_amount: String(p.size_amount ?? ""), cost: String(p.cost ?? "") }); setEditing(p.product_id); };
  const cancel = () => { setEditing(null); setDraft(blank); };

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      name: draft.name.trim(), brand: draft.brand.trim(), type: draft.type, shade: draft.shade.trim(),
      size_amount: Number(draft.size_amount) || 0, size_unit: draft.size_unit, cost: Number(draft.cost) || 0,
      is_active: draft.is_active !== false,
    };
    try {
      if (editing === "new") await api.post("/color-products", payload);
      else await api.put(`/color-products/${editing}`, payload);
      toast.success("Saved");
      cancel(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product from the library?")) return;
    try { await api.delete(`/color-products/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete"); }
  };

  const saveMultiplier = async () => {
    try {
      const r = await api.put("/color-bar/settings", { charge_multiplier: Number(multInput) || 2.75 });
      setMultiplier(r.data.charge_multiplier); setMultInput(String(r.data.charge_multiplier));
      toast.success("Charge multiplier updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };

  const [importing, setImporting] = useState(false);
  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/color-products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { added, error_count } = r.data;
      toast.success(`Imported ${added} product${added !== 1 ? "s" : ""}${error_count ? ` · ${error_count} row(s) skipped` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setImporting(false);
  };

  const downloadTemplate = () => {
    const csv = "name,brand,type,shade,size_amount,size_unit,cost\nShades EQ 7N,Redken,color,7N,60,g,9\nBlondor,Wella,lightener,,400,g,24\nWelloxon 20 vol,Wella,developer,20 vol,1000,ml,12\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "color-products-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const Field = ({ label, children }) => (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 font-['Manrope'] mb-1">{label}</p>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f7f4ee] via-[#faf7f1] to-[#eef4f2] p-6 lg:p-8" data-testid="color-library-page">
      <div className="max-w-[1200px] mx-auto space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-fuchsia-600 font-['Manrope']">
              <Palette className="w-3.5 h-3.5" /> Color Bar
            </div>
            <h1 className="font-['Outfit'] text-4xl lg:text-5xl font-light text-[#072032] tracking-tight mt-1">Color Library</h1>
            <p className="text-sm text-stone-600 mt-1 font-['Manrope']">Manage the products stylists pick from at the Color Bar, and how charges are suggested.</p>
          </div>
          <a href="/color-bar" target="_blank" rel="noreferrer" data-testid="open-colorbar-kiosk"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#072032] text-white text-sm font-semibold font-['Manrope'] hover:bg-[#0a2c45] self-start">
            <ExternalLink className="w-4 h-4" /> Open Color Bar kiosk
          </a>
        </div>

        {/* Charge multiplier */}
        {isManager && (
          <div className="bg-white border border-stone-200 rounded-2xl p-5 flex flex-wrap items-end gap-4" data-testid="charge-multiplier-card">
            <Field label="Suggested charge multiplier">
              <div className="flex items-center gap-2">
                <input type="number" step="0.25" min="1" value={multInput} onChange={(e) => setMultInput(e.target.value)}
                  data-testid="multiplier-input"
                  className="w-24 h-10 rounded-lg border border-stone-200 px-3 text-[#072032] font-semibold text-center focus:outline-none focus:ring-2 focus:ring-fuchsia-300" />
                <span className="text-sm text-stone-500 font-['Manrope']">× product cost</span>
                <Button onClick={saveMultiplier} data-testid="save-multiplier" className="h-10 rounded-full bg-[#072032] text-white">Save</Button>
              </div>
            </Field>
            <p className="text-xs text-stone-500 font-['Manrope']">Example: a {money(10)} bowl suggests a {money(10 * multiplier)} charge.</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2">
          {[["products", "Products"], ["formulas", "Recent formulas"], ["waste", "Waste report"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`color-tab-${k}`}
              className={`h-10 px-5 rounded-full text-sm font-semibold font-['Manrope'] border ${tab === k ? "bg-[#072032] text-white border-[#072032]" : "bg-white border-stone-200 text-stone-600"}`}>{l}</button>
          ))}
        </div>

        {tab === "products" && (
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="products-table">
            <div className="flex items-center justify-between p-4 border-b border-stone-100">
              <p className="text-sm font-semibold text-[#072032] font-['Outfit']">{products.length} product{products.length !== 1 ? "s" : ""}</p>
              {isManager && editing !== "new" && (
                <div className="flex items-center gap-2">
                  <button onClick={downloadTemplate} data-testid="csv-template-btn"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-stone-200 text-stone-600 text-sm font-semibold font-['Manrope'] hover:bg-stone-50">
                    <FileDown className="w-4 h-4" /> Template
                  </button>
                  <label data-testid="csv-import-btn"
                    className={`cursor-pointer inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-stone-200 text-stone-700 text-sm font-semibold font-['Manrope'] hover:bg-stone-50 ${importing ? "opacity-50 pointer-events-none" : ""}`}>
                    <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import CSV"}
                    <input type="file" accept=".csv,text/csv" className="hidden" data-testid="csv-import-input"
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; importCsv(f); }} />
                  </label>
                  <Button onClick={startNew} data-testid="add-product-btn" className="h-9 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white"><Plus className="w-4 h-4 mr-1" />Add product</Button>
                </div>
              )}
            </div>

            {editing === "new" && (
              <ProductEditor draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} isNew />
            )}

            <div className="divide-y divide-stone-100">
              {products.length === 0 && editing !== "new" && (
                <p className="p-8 text-center text-stone-400 font-['Manrope']">No products yet. Add your color lines, developers and lighteners so stylists can pick them at the Color Bar.</p>
              )}
              {products.map((p) => (
                editing === p.product_id ? (
                  <ProductEditor key={p.product_id} draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} />
                ) : (
                  <div key={p.product_id} className="flex items-center gap-4 p-4" data-testid={`product-row-${p.product_id}`}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      p.type === "developer" ? "bg-sky-100 text-sky-700" : p.type === "lightener" ? "bg-amber-100 text-amber-700" :
                      p.type === "toner" ? "bg-rose-100 text-rose-700" : p.type === "additive" ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700"}`}>{p.type}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#072032] font-semibold font-['Outfit'] truncate">{p.brand ? `${p.brand} · ` : ""}{p.name}{p.shade ? ` (${p.shade})` : ""}</p>
                      <p className="text-xs text-stone-500 font-['Manrope']">{p.size_amount}{p.size_unit} · {money(p.cost)} pack · {money(p.cost_per_gram)}/g</p>
                    </div>
                    {isManager && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => startEdit(p)} data-testid={`edit-product-${p.product_id}`} className="w-9 h-9 rounded-lg hover:bg-stone-100 text-stone-500 flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(p.product_id)} data-testid={`delete-product-${p.product_id}`} className="w-9 h-9 rounded-lg hover:bg-rose-50 text-stone-400 hover:text-rose-600 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {tab === "formulas" && (
          <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3" data-testid="formulas-list">
            {formulas.length === 0 && <p className="p-8 text-center text-stone-400 font-['Manrope']">No formulas logged yet. They'll appear here as stylists save them at the Color Bar.</p>}
            {formulas.map((f) => (
              <div key={f.formula_id} className="border border-stone-100 rounded-xl p-4" data-testid={`formula-${f.formula_id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[#072032] font-semibold font-['Outfit']">{f.client_name || "Client"}</p>
                  <span className="text-sm text-stone-500 font-['Manrope']">{money(f.total_cost)} cost · {money(f.suggested_charge)} charge</span>
                </div>
                <p className="text-xs text-stone-500 font-['Manrope']">{f.service_date} · {f.stylist_name}{f.processing_minutes ? ` · ${f.processing_minutes} min` : ""}{f.developer ? ` · ${f.developer}` : ""}</p>
                {(f.bowls || []).map((b) => (
                  <p key={b.bowl_id} className="text-xs text-stone-600 font-['Manrope'] mt-1 flex items-start gap-1">
                    <Beaker className="w-3.5 h-3.5 text-fuchsia-400 mt-0.5 shrink-0" />
                    <span><span className="font-semibold">{b.label}:</span> {(b.components || []).map((c) => `${c.product_name} ${c.amount}${c.unit}`).join(" + ")}</span>
                  </p>
                ))}
                {f.notes && <p className="text-xs text-stone-400 italic font-['Manrope'] mt-1">"{f.notes}"</p>}
                {(f.photos || []).length > 0 && (
                  <div className="flex gap-2 mt-2" data-testid={`formula-photos-${f.formula_id}`}>
                    {f.photos.map((p) => (
                      <AuthImg key={p.photo_id} formulaId={f.formula_id} photoId={p.photo_id} label={p.label} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "waste" && (
          <div className="space-y-5" data-testid="waste-report">
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-500 font-['Manrope']">Period:</span>
              {[[7, "7 days"], [30, "30 days"], [90, "90 days"]].map(([d, l]) => (
                <button key={d} onClick={() => setWasteDays(d)} data-testid={`waste-range-${d}`}
                  className={`h-8 px-3 rounded-full text-xs font-semibold font-['Manrope'] border ${wasteDays === d ? "bg-amber-500 text-white border-amber-500" : "bg-white border-stone-200 text-stone-600"}`}>{l}</button>
              ))}
            </div>

            {!waste ? <p className="text-stone-400 text-sm font-['Manrope']">Loading…</p> : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[["Product cost", waste.totals.product_cost, "text-stone-800"],
                    ["Wasted", waste.totals.waste_cost, "text-amber-600"],
                    ["Actually used", waste.totals.used_cost, "text-emerald-600"],
                    ["Waste rate", `${waste.totals.waste_pct}%`, "text-amber-600"]].map(([lbl, val, cls], i) => (
                    <div key={i} className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`waste-stat-${i}`}>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 font-['Manrope']">{lbl}</p>
                      <p className={`font-['Outfit'] text-2xl font-semibold tabular-nums ${cls}`}>{typeof val === "number" ? money(val) : val}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-stone-500 font-['Manrope']">{waste.reweighed_count} of {waste.total_formulas} formula{waste.total_formulas !== 1 ? "s" : ""} reweighed in this period. Only reweighed formulas count toward waste.</p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="waste-by-stylist">
                    <h3 className="font-['Outfit'] text-sm font-bold uppercase tracking-widest text-[#072032] mb-3">By stylist</h3>
                    {waste.by_stylist.length === 0 ? <p className="text-sm text-stone-400 font-['Manrope']">No waste logged yet.</p> : (
                      <div className="space-y-3">
                        {waste.by_stylist.map((s) => {
                          const max = waste.by_stylist[0].waste_cost || 1;
                          return (
                            <div key={s.stylist_id} data-testid={`waste-stylist-${s.stylist_id}`}>
                              <div className="flex justify-between text-sm font-['Manrope']">
                                <span className="text-slate-700 font-semibold">{s.name}</span>
                                <span className="text-amber-600 tabular-nums">{money(s.waste_cost)} · {s.waste_pct}%</span>
                              </div>
                              <div className="h-2 rounded-full bg-stone-100 mt-1 overflow-hidden">
                                <div className="h-full bg-amber-400 rounded-full" style={{ width: `${Math.max(4, (s.waste_cost / max) * 100)}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="waste-by-product">
                    <h3 className="font-['Outfit'] text-sm font-bold uppercase tracking-widest text-[#072032] mb-3">By product</h3>
                    {waste.by_product.length === 0 ? <p className="text-sm text-stone-400 font-['Manrope']">No waste logged yet.</p> : (
                      <div className="space-y-2">
                        {waste.by_product.map((p) => (
                          <div key={p.product_id} className="flex items-center justify-between" data-testid={`waste-product-${p.product_id}`}>
                            <div className="min-w-0">
                              <p className="text-sm text-slate-800 font-semibold font-['Outfit'] truncate">{p.name}</p>
                              <p className="text-[11px] text-stone-400 font-['Manrope'] capitalize">{p.type} · {p.waste_grams}g wasted</p>
                            </div>
                            <span className="text-amber-600 text-sm font-['Manrope'] tabular-nums">{money(p.waste_cost)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

function AuthImg({ formulaId, photoId, label }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let revoked = false; let objUrl = "";
    api.get(`/color-formulas/${formulaId}/photos/${photoId}`, { responseType: "blob" })
      .then((res) => { if (revoked) return; objUrl = URL.createObjectURL(res.data); setUrl(objUrl); })
      .catch(() => {});
    return () => { revoked = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [formulaId, photoId]);
  return (
    <div className="relative">
      {url
        ? <img src={url} alt={label} className="w-16 h-16 rounded-lg object-cover border border-stone-200" />
        : <div className="w-16 h-16 rounded-lg bg-stone-100 animate-pulse" />}
      <span className="absolute bottom-0 inset-x-0 text-[8px] text-center text-white bg-black/40 rounded-b-lg capitalize">{label}</span>
    </div>
  );
}

function ProductEditor({ draft, setDraft, onSave, onCancel, isNew }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const inp = "w-full h-10 rounded-lg border border-stone-200 px-3 text-sm text-[#072032] font-['Manrope'] focus:outline-none focus:ring-2 focus:ring-fuchsia-300";
  return (
    <div className="p-4 bg-fuchsia-50/40 border-b border-stone-100 grid grid-cols-2 sm:grid-cols-7 gap-3 items-end" data-testid="product-editor">
      <div className="col-span-2 sm:col-span-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">Name</p>
        <input value={draft.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="pe-name" placeholder="Shades EQ" />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">Brand</p>
        <input value={draft.brand} onChange={(e) => set("brand", e.target.value)} className={inp} data-testid="pe-brand" placeholder="Redken" />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">Type</p>
        <select value={draft.type} onChange={(e) => set("type", e.target.value)} className={inp} data-testid="pe-type">
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">Shade</p>
        <input value={draft.shade} onChange={(e) => set("shade", e.target.value)} className={inp} data-testid="pe-shade" placeholder="7N" />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">Pack size</p>
        <div className="flex gap-1">
          <input type="number" value={draft.size_amount} onChange={(e) => set("size_amount", e.target.value)} className={inp} data-testid="pe-size" placeholder="60" />
          <select value={draft.size_unit} onChange={(e) => set("size_unit", e.target.value)} className="h-10 rounded-lg border border-stone-200 px-1 text-sm" data-testid="pe-unit">
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1">Pack cost $</p>
        <input type="number" step="0.01" value={draft.cost} onChange={(e) => set("cost", e.target.value)} className={inp} data-testid="pe-cost" placeholder="9.00" />
      </div>
      <div className="col-span-2 sm:col-span-7 flex gap-2 justify-end">
        <Button onClick={onCancel} variant="outline" className="h-9 rounded-full" data-testid="pe-cancel"><X className="w-4 h-4 mr-1" />Cancel</Button>
        <Button onClick={onSave} className="h-9 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="pe-save"><Save className="w-4 h-4 mr-1" />{isNew ? "Add" : "Save"}</Button>
      </div>
    </div>
  );
}
